import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  ArrowRight,
  ShieldCheck,
  Linkedin,
  Globe,
  Cookie,
} from "lucide-react";
import { BrandLogoHorizontalNavy, BrandLogoHorizontalWhite } from "@/assets/brand";
import pymeBadge from "@/assets/registro-pyme.webp";
import { useLanguage } from "@/contexts/LanguageContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useDemoDialog } from "@/components/DemoRequestProvider";
import { openConsentPreferences } from "@/lib/cookieConsent";


const FintechFooter = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { resolvedTheme } = useTheme();
  const { open: openDemo } = useDemoDialog();
  const isDark = resolvedTheme === "dark";
  const logo = isDark ? BrandLogoHorizontalWhite : BrandLogoHorizontalNavy;

  const cols = t.footer.columns;
  const isEn = language === "en";
  const productLinks = [
    { name: "FONDEA", href: "/fondea", desc: isEn ? "Liquidity for businesses and SMEs" : "Liquidez para empresas y PYMES" },
    { name: "SOLVENN", href: "/white-label", desc: isEn ? "Infrastructure for financial institutions" : "Infraestructura para instituciones financieras" },
  ];
  const solutionLinks = [
    { name: "Factoring", href: "/solutions/factoring" },
    { name: "Confirming", href: "/solutions/confirming" },
    { name: isEn ? "B2B payments" : "Pagos B2B", href: "/solutions/payments" },
    { name: isEn ? "Auctions" : "Subastas", href: "/solutions/auctions" },
    { name: isEn ? "Integrations" : "Integraciones", href: "/solutions/integrations" },
  ];
  const companyLinks = [
    { name: isEn ? "About IBYLIT" : "Acerca de IBYLIT", href: "/company/about" },
    { name: isEn ? "Case studies" : "Casos de éxito", href: "/resources/case-studies" },
    { name: isEn ? "Resources" : "Recursos", href: "/resources/blog" },
    { name: isEn ? "Contact" : "Contacto", href: "/contact" },
  ];
  const legalLinks = [
    { name: isEn ? "Privacy" : "Privacidad", href: "/legal/privacy" },
    { name: isEn ? "Terms" : "Términos", href: "/legal/terms" },
    { name: isEn ? "Security" : "Seguridad", href: "/security/compliance" },
  ];

  return (
    <footer className="relative bg-muted/30 dark:bg-[hsl(230_30%_4%)] border-t border-border/40">
      {/* Top rail — IBYLIT signature */}
      <div className="rail-line" aria-hidden="true" />

      <div className="enterprise-container py-16 lg:py-20">
        {/* MAIN GRID */}
        <nav aria-label={t.a11y.footerNavigation} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-8 gap-y-12">
          {/* BRAND — col-span-2 */}
          <div className="col-span-2 lg:col-span-2">
            <img src={logo} alt="Logo IBYLIT" loading="lazy" decoding="async" width={172} height={60} className="h-12 w-auto mb-6" />
            <div className="eyebrow-mono mb-3 text-primary-strong">
              {t.footer.eyebrow}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-xs">
              {t.footer.description}
            </p>
            <Button onClick={openDemo} size="sm" variant="default" className="group">
              {t.footer.ctaDemo}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Button>
          </div>

          {/* PRODUCTS — the two product lines */}
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center gap-2 mb-5">
              <div className="h-px w-6 bg-primary/60" aria-hidden="true" />
              <h3 className="eyebrow-mono text-foreground">{isEn ? "Products" : "Productos"}</h3>
            </div>
            <ul className="space-y-1">
              {productLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="group block rounded-lg px-2 py-2 -mx-2 hover:bg-primary/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      {item.name}
                      <ArrowRight className="size-3 text-primary opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground leading-snug">{item.desc}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <FooterColumn title={isEn ? "Solutions" : "Soluciones"} items={solutionLinks} />
          <FooterColumn title={cols.company.title} items={companyLinks} />
          <FooterColumn title="Legal" items={legalLinks} />
        </nav>

        {/* SUBFOOTER */}
        <div className="mt-16 pt-8 border-t border-border/50">
          <div className="flex flex-col lg:flex-row items-center lg:items-center justify-between gap-6 text-center lg:text-left">
            {/* Center: badges pill (left slot on desktop) */}

            {/* Center: badges pill */}
            <ul
              aria-label="Certificaciones y cumplimiento"
              className="order-1 lg:order-1 inline-flex flex-wrap justify-center items-center gap-2 px-4 py-2 rounded-full border border-border/60 bg-background/40 backdrop-blur-sm list-none m-0"
            >
              {t.footer.badges.map((b, i) => (
                <li key={b} className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground font-normal normal-case font-sans">
                    {b}
                  </span>
                  {i < t.footer.badges.length - 1 && (
                    <span aria-hidden="true" className="text-primary-strong/40 text-[10px]">•</span>
                  )}
                </li>
              ))}
            </ul>

            {/* Right: social + theme + lang + legal */}
            <div className="flex flex-wrap items-center justify-center gap-4 order-2 lg:order-2">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  {t.footer.follow}
                </span>
                <a
                  href="https://www.linkedin.com/company/ibylit-fintech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                >
                  <Linkedin className="size-4" aria-hidden="true" />
                </a>
              </div>

              <div className="h-4 w-px bg-border" aria-hidden="true" />

              <ThemeToggle />

              <button
                type="button"
                onClick={toggleLanguage}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                aria-label={`${language.toUpperCase()} — ${t.a11y.toggleLanguage}`}
              >
                <Globe className="size-3.5" aria-hidden="true" />
                {language.toUpperCase()}
              </button>

              <div className="h-4 w-px bg-border" aria-hidden="true" />

              <button
                type="button"
                onClick={openConsentPreferences}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                aria-label={t.cookieConsent.actions.preferences}
              >
                <Cookie className="size-3.5" aria-hidden="true" />
                {t.cookieConsent.actions.preferences}
              </button>

            </div>
          </div>

          {/* Trust badges row — PYME CR + ImmuniWeb SSL */}
          <div className="flex flex-wrap items-center justify-center lg:justify-end gap-3 mt-6">
            <a
              href="https://www.meic.go.cr/tramites-y-servicios/pymes-activas/"
              target="_blank"
              rel="noopener noreferrer"
              title="MEIC — Registro PYME Costa Rica"
              className="group inline-flex items-center gap-2 h-[40px] md:h-[44px] px-3 rounded-md border border-border/60 bg-background/60 backdrop-blur-sm opacity-85 hover:opacity-100 transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <img
                src={pymeBadge}
                alt="Sello oficial Registro PYME — Ministerio de Economía de Costa Rica"
                loading="lazy"
                decoding="async"
                width="64"
                height="32"
                className="h-7 md:h-8 w-auto"
              />
              <span className="flex flex-col leading-none">
                <span className="eyebrow-mono text-[9px] text-foreground/80 tracking-wider">
                  MEIC
                </span>
                <span className="text-[11px] font-semibold text-foreground mt-0.5">
                  Registro PYME
                </span>
              </span>
            </a>

            <a
              href="https://www.immuniweb.com/websec/ibylit.com/CTc5Bdld/"
              target="_blank"
              rel="noopener noreferrer"
              title="ImmuniWeb SSL Security Test — Grade A — ibylit.com"
              className="group inline-flex items-stretch h-[40px] md:h-[44px] overflow-hidden rounded-md border border-border/60 bg-background/40 backdrop-blur-sm opacity-85 hover:opacity-100 transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {/* Left: shield + label */}
              <span className="flex items-center gap-2 px-3 bg-muted/40">
                <ShieldCheck className="size-4 text-primary" aria-hidden="true" />
                <span className="flex flex-col leading-none">
                  <span className="eyebrow-mono text-[9px] text-foreground/80 tracking-wider">
                    ImmuniWeb
                  </span>
                  <span className="text-[11px] font-semibold text-foreground mt-0.5">
                    SSL Security Test
                  </span>
                </span>
              </span>
              {/* Right: grade */}
              <span className="flex items-center justify-center px-3 bg-[hsl(214_100%_30%)] text-white text-sm font-bold tracking-tight">
                A
              </span>
            </a>
          </div>

          {/* Copyright — always last */}
          <p className="text-xs text-muted-foreground text-center mt-6">
            © {new Date().getFullYear()} IBYLIT. {t.footer.copyright}
          </p>

        </div>
      </div>
    </footer>
  );
};

interface ColItem {
  name: string;
  href: string;
}

const FooterColumn = ({ title, items }: { title: string; items: ColItem[] }) => (
  <div className="col-span-1">
    <div className="flex items-center gap-2 mb-5">
      <div className="h-px w-6 bg-primary/60" aria-hidden="true" />
      <h3 className="eyebrow-mono text-foreground">{title}</h3>
    </div>
    <ul className="space-y-2.5">
      {items.map((link) => (
        <li key={link.href}>
          <Link
            to={link.href}
            className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
          >
            <span>{link.name}</span>
            <ArrowRight className="size-3 text-primary opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default FintechFooter;
